import * as _ from 'lodash';
import { useSelector } from 'react-redux';
import { ProjectType, ReportCategories } from '../ReportGeneration/utils';
import type { ProjectContext, ReportCategory } from '../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from './shared/types';
import LicensingFilters from './LicensingFilters';
import type { SetState } from '../../../../types';
import SBOMFilters from './SBOMFilters';
import { put } from '../../../../redux/api';
import { nuc } from '../../../../utils';
import { getProjectType, getReportContext } from '../../../../redux/reportGeneration/selectors';
import Locator from '../../../../../shared/Locator';
import { useAddToast } from '../../../../contexts/ToastContext';
import useReportContainer from '../ReportGeneration/context/useReportContainer';
import FixPlanFilters from './FixPlanFilters';

type ReportFiltersProps = {
  isApplyButtonDisabled: boolean;
  reportType: ReportCategory;
  format: FormatName | null;
  setFormat: SetState<FormatName | null>;
  includeOptions: IncludeFilterOption[];
  setIncludeOptions: SetState<IncludeFilterOption[]>;
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  setDependencyMetadataOptions: SetState<DependencyMetadataFilterOption[]>;
  setIsDirty: SetState<boolean>;
};

const ReportFilters = ({
  isApplyButtonDisabled,
  reportType,
  format,
  setFormat,
  includeOptions: appliedIncludeOptions,
  setIncludeOptions,
  dependencyMetadataOptions: appliedDependencyMetadataOptions,
  setDependencyMetadataOptions,
  setIsDirty,
}: ReportFiltersProps) => {
  const { setDependencyInfoOptions } = useReportContainer();
  const projectType = useSelector(getProjectType);
  const currentProjectContext = useSelector(getReportContext) as ProjectContext;
  const isSBOM = reportType === ReportCategories.SBOM;
  const isLicensing = reportType === ReportCategories.Licensing;
  const isFixPlan = reportType === ReportCategories.FixPlan;
  const addToast = useAddToast();
  const onApply = async ({
    format: newFormat,
    includeOptions,
    dependencyMetadataOptions,
  }: {
    format: FormatName;
    includeOptions: IncludeFilterOption[];
    dependencyMetadataOptions: DependencyMetadataFilterOption[];
  }) => {
    setFormat((previousFormat) => (previousFormat === newFormat ? previousFormat : newFormat));
    setIncludeOptions((previousOptions) =>
      _.isEqual(previousOptions, includeOptions) ? previousOptions : includeOptions,
    );
    setDependencyMetadataOptions((previousOptions) =>
      _.isEqual(previousOptions, dependencyMetadataOptions) ? previousOptions : dependencyMetadataOptions,
    );
    if (projectType === ProjectType.Project) {
      try {
        const locator = Locator.parsePackageString(currentProjectContext.revision);
        await put(`/api/projects/${nuc(locator)}`, { bom_column_settings: dependencyMetadataOptions });
        setDependencyInfoOptions(dependencyMetadataOptions);
      } catch (error) {
        addToast({
          content: 'Failed to save dependency metadata',
          type: 'error',
        });
      }
    }
  };
  if (isSBOM) {
    return (
      <SBOMFilters
        onApply={onApply}
        appliedFormat={format}
        includeOptions={appliedIncludeOptions}
        dependencyMetadataOptions={appliedDependencyMetadataOptions}
        setIsDirty={setIsDirty}
        isApplyButtonDisabled={isApplyButtonDisabled}
      />
    );
  }
  if (isLicensing) {
    return (
      <LicensingFilters
        onApply={onApply}
        appliedFormat={format}
        includeOptions={appliedIncludeOptions}
        dependencyMetadataOptions={appliedDependencyMetadataOptions}
        setIsDirty={setIsDirty}
        isApplyButtonDisabled={isApplyButtonDisabled}
      />
    );
  }
  if (isFixPlan) {
    return (
      <FixPlanFilters
        onApply={onApply}
        appliedFormat={format}
        setIsDirty={setIsDirty}
        isApplyButtonDisabled={isApplyButtonDisabled}
      />
    );
  }

  return null;
};

export default ReportFilters;
